// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ProjectPage.test.tsx
import { type AskPunttOpenItem } from "@mg/schemas/src/christo/askPunttOpenItems";
import { Button, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { nudgeKey, OpenItemRows } from "./AskV2OpenItemRow";
import { AskV2OpenItemsGroups, paneItems } from "./AskV2OpenItemsGroups";
import { AskV2OpenItemsSplit } from "./AskV2OpenItemsSplit";
import { AskV2ProjectDoneBadge } from "./AskV2ProjectDoneBadge";
import {
  type UseProjectOpenItemsOptions,
  useProjectOpenItems,
} from "./useProjectOpenItems";

import { ASK_V2_RAIL_SECTION_CLASS } from "../rail/AskV2RailSection";
type Member = {
  _id: string;
  name?: string | null;
  email?: string | null;
};

export function AskV2OpenItems({
  projectId,
  members,
  onAttachOpenItem,
  reviewHasRun = false,
  projectOpenItemsOptions,
  currentUserId,
  projectOwnerId = "",
  projectOwnerName = "",
  railHost,
  splitHost,
  onOpenSplit,
  onCloseSplit,
}: {
  projectId: string;
  members: Member[];
  onAttachOpenItem: (item: AskPunttOpenItem) => void;
  reviewHasRun?: boolean;
  projectOpenItemsOptions?: UseProjectOpenItemsOptions;
  currentUserId: string | undefined;
  projectOwnerId?: string;
  projectOwnerName?: string;
  railHost: HTMLElement | null;
  splitHost: HTMLElement | null;
  onOpenSplit: () => void;
  onCloseSplit: () => void;
}) {
  const { t } = useTranslation("ui");
  const { query, assign, nudge } = useProjectOpenItems(
    projectId,
    projectOpenItemsOptions,
  );
  const openItems = (query.data?.openItems ?? []).filter(
    (item) => item.status !== "cleared",
  );
  // why: the rail count and the split use the pane list. Counting findings opens "Open items N" onto an empty pane.
  const items = paneItems(openItems);
  useEffect(() => {
    if (!query.isSuccess) return;
    if (splitHost != null && items.length === 0) onCloseSplit();
  }, [query.isSuccess, splitHost, items.length, onCloseSplit]);
  // why: "Nudged just now" is a fact about the owner we pinged, not about the
  // row. Keyed by row alone, the badge outlived a handover — a late resolve,
  // a refetch that landed after it, or a poll naming a new owner all left it
  // on someone who was never pinged, with no Nudge control to reach them.
  const [nudgedOwnerByItemId, setNudgedOwnerByItemId] = useState<
    ReadonlyMap<string, string>
  >(() => new Map());
  const [pendingNudges, setPendingNudges] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [handedItemIds, setHandedItemIds] = useState<Set<string>>(
    () => new Set(),
  );
  const mayAssignOpenItems = query.data?.mayAssignOpenItems === true;
  // why: the light background marks the row last attached to the composer — painting every row, or keeping it after a different attach, marks the wrong question.
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  if (!query.isSuccess) return null;

  const doneAt = query.data.doneAt;
  const doneBadge = (
    <AskV2ProjectDoneBadge doneAt={doneAt} testId="ask-v2-project-done-badge" />
  );

  // why: before a review finishes, an empty rollup is not "no open items" —
  // the list has not been produced yet. Hide the section until then.
  if (items.length === 0 && !reviewHasRun) {
    // why: returning null here drops the done badge once every row is cleared.
    // Portal only: this component sits outside the sidebar, and an inline
    // badge would render above the chat.
    return railHost != null ? createPortal(doneBadge, railHost) : null;
  }

  // why: the count button is the control — a section toggle repeats "Open items" and hides the only action.
  const railSection =
    items.length > 0 || reviewHasRun ? (
      <div
        className={`${ASK_V2_RAIL_SECTION_CLASS} px-4 py-3`}
        data-testid="ask-v2-open-items-rail"
      >
        {items.length === 0 ? (
          <>
            {doneBadge}
            <Text size="2" data-testid="ask-v2-open-items-empty">
              {t("askV2.openItems.empty")}
            </Text>
          </>
        ) : (
          <div className="flex flex-col gap-3">
            {doneAt != null ? doneBadge : null}
            <Button
              type="button"
              variant="soft"
              className="w-full"
              data-testid="ask-v2-open-items-cta"
              onClick={onOpenSplit}
            >
              {t("askV2.openItems.listName")}{" "}
              <span data-testid="ask-v2-open-items-cta-count">
                {items.length}
              </span>
            </Button>
          </div>
        )}
      </div>
    ) : null;

  return (
    <>
      {railHost != null && railSection != null
        ? createPortal(railSection, railHost)
        : null}
      {splitHost != null && items.length > 0
        ? createPortal(
            <AskV2OpenItemsSplit onClose={onCloseSplit}>
              {doneAt != null ? doneBadge : null}
              <AskV2OpenItemsGroups
                items={items}
                currentUserId={currentUserId}
                renderRows={(rows) => (
                  <OpenItemRows
                    projectId={projectId}
                    items={rows}
                    members={members}
                    mayAssign={mayAssignOpenItems}
                    assignPending={assign.isPending}
                    handedItemIds={handedItemIds}
                    projectOwnerId={projectOwnerId}
                    projectOwnerName={projectOwnerName}
                    currentUserId={currentUserId}
                    pendingNudges={pendingNudges}
                    nudgedOwnerByItemId={nudgedOwnerByItemId}
                    onHandToOwner={(itemId) => {
                      if (projectOwnerId.length === 0) return;
                      void assign
                        .mutateAsync({
                          itemId,
                          ownerUserId: projectOwnerId,
                        })
                        .then(
                          () => {
                            setHandedItemIds((current) => {
                              const next = new Set(current);
                              next.add(itemId);
                              return next;
                            });
                          },
                          () => {
                            // why: a rejected handover left the row with its
                            // owner. Marking it handed would claim otherwise.
                          },
                        );
                    }}
                    onAssign={(itemId, ownerUserId) => {
                      setHandedItemIds((current) => {
                        const next = new Set(current);
                        next.delete(itemId);
                        return next;
                      });
                      // why: mutate, not a voided mutateAsync — a rejected
                      // assign otherwise escapes as an unhandled rejection.
                      assign.mutate({ itemId, ownerUserId });
                    }}
                    onNudge={(itemId, ownerUserId) => {
                      const key = nudgeKey(itemId, ownerUserId);
                      setPendingNudges((current) => new Set(current).add(key));
                      void nudge
                        .mutateAsync(itemId)
                        .then(
                          () => {
                            setNudgedOwnerByItemId((current) =>
                              new Map(current).set(itemId, ownerUserId),
                            );
                          },
                          () => {
                            // why: a failed nudge pinged nobody. Leaving the
                            // badge unset re-offers the control once it settles.
                          },
                        )
                        .finally(() => {
                          setPendingNudges((current) => {
                            const next = new Set(current);
                            next.delete(key);
                            return next;
                          });
                        });
                    }}
                    selectedItemId={selectedItemId}
                    onAttach={(item) => {
                      setSelectedItemId(item.id);
                      onAttachOpenItem(item);
                    }}
                  />
                )}
              />
            </AskV2OpenItemsSplit>,
            splitHost,
          )
        : null}
    </>
  );
}
